import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { cn } from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import { type JSX, useMemo } from 'react'
import { match } from 'ts-pattern'
import {
  Combobox,
  type ComboboxOption,
  type ComboboxProps,
} from '#src/components/core/combobox/Combobox.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'

type Priority = NonNullable<StandardTask['priority']> | null

interface TaskPriorityComboboxProps extends Omit<
  ComboboxProps<Priority>,
  'options'
> {
  selected: Priority
}

export function TaskPriorityCombobox({
  selected,
  ...delegated
}: TaskPriorityComboboxProps): JSX.Element {
  const options: ComboboxOption<Priority>[] = useMemo(
    () => [
      {
        value: null,
        label: (
          <TaskPriorityOption
            priority={undefined}
            isSelected={selected === undefined || selected === null}
          />
        ),
        keywords: [priorityLabelText(undefined)],
      },
      {
        value: 'stat',
        label: (
          <TaskPriorityOption
            priority="stat"
            isSelected={selected === 'stat'}
          />
        ),
        keywords: [priorityLabelText('stat')],
      },
      {
        value: 'urgent',
        label: (
          <TaskPriorityOption
            priority="urgent"
            isSelected={selected === 'urgent'}
          />
        ),
        keywords: [priorityLabelText('urgent')],
      },
      {
        value: 'asap',
        label: (
          <TaskPriorityOption
            priority="asap"
            isSelected={selected === 'asap'}
          />
        ),
        keywords: [priorityLabelText('asap')],
      },
      {
        value: 'routine',
        label: (
          <TaskPriorityOption
            priority="routine"
            isSelected={selected === 'routine'}
          />
        ),
        keywords: [priorityLabelText('routine')],
      },
    ],
    [selected]
  )
  return <Combobox options={options} {...delegated} />
}

export function TaskPriorityOption({
  priority,
  isSelected,
}: {
  priority: StandardTask['priority']
  isSelected: boolean
}): JSX.Element {
  const definition = priorityDefinition(priority)
  return (
    <div className="flex flex-1 items-center justify-between">
      <div className="ml-0.5 flex items-center gap-2">
        <TaskPriorityIcon priority={priority} className="shrink-0" />
        <div className="flex items-center gap-1">
          <span>{priorityLabelText(priority)}</span>
          {definition && (
            <span className="text-xs text-muted-foreground">{definition}</span>
          )}
        </div>
      </div>
      <IconCheck
        className={cn(
          'mr-0.5 size-4 shrink-0 opacity-0',
          isSelected && 'opacity-100'
        )}
      />
    </div>
  )
}

function priorityLabelText(priority: StandardTask['priority']): string {
  return match(priority)
    .with('stat', () => 'STAT')
    .with('urgent', () => 'High')
    .with('asap', () => 'Medium')
    .with('routine', () => 'Low')
    .with(undefined, () => 'No Priority')
    .exhaustive()
}

export function priorityDefinition(
  priority: StandardTask['priority']
): string | undefined {
  return match(priority)
    .with('stat', () => 'Same day')
    .with('urgent', () => 'Within 1 week')
    .with('asap', () => 'Within 2 weeks')
    .with('routine', () => 'Within 4 weeks')
    .with(undefined, () => undefined)
    .exhaustive()
}
